import React, { useEffect, useState, useRef } from 'react';
import { X, Sparkles, Check, Zap, PersonStanding, Image, Mic, CloudLightning, RefreshCw, Gem, Coins, ChevronRight, Lock, Play, Pause, Save, Share2, Wand2, Lightbulb, Brain, Crown, Flame, Heart, Snowflake, ShieldCheck, Palette, BadgeCheck, Loader, Globe, PenTool, BookOpen, Rocket, Sliders, Maximize, Minimize, Eye, EyeOff, Settings, Shuffle } from 'lucide-react';
// Define types for the ShapeShift feature
type ShiftTab = 'persona' | 'scene' | 'voice' | 'mood';
type ShiftPreset = {
  id: string;
  name: string;
  description: string;
  image: string;
  cost: number;
  isPremium: boolean;
  traits?: Record<string, number>;
  sceneProps?: Record<string, string>;
  voiceProps?: Record<string, any>;
  moodProps?: Record<string, any>;
  badge?: string;
  effect?: string;
};
interface ShapeShiftModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (result: any) => void;
  ritualId?: string | null;
  angelId?: string;
  angelName?: string;
  inPlayroom?: boolean;
}
export const ShapeShiftModal: React.FC<ShapeShiftModalProps> = ({
  isOpen,
  onClose,
  onComplete,
  ritualId = null,
  angelId = '1',
  angelName = 'Sophia',
  inPlayroom = false
}) => {
  const [activeTab, setActiveTab] = useState<ShiftTab>('persona');
  const [prompt, setPrompt] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<ShiftPreset | null>(null);
  const [customTraits, setCustomTraits] = useState({
    confidence: 50,
    playfulness: 50,
    intensity: 50,
    empathy: 50,
    creativity: 50,
    dominance: 50,
    sensuality: 50,
    intellect: 50
  });
  const [sceneSettings, setSceneSettings] = useState({
    outfit: 'casual',
    background: 'bedroom',
    lighting: 'soft',
    mood: 'relaxed',
    time: 'day',
    weather: 'clear',
    atmosphere: 'cozy'
  });
  const [voiceSettings, setVoiceSettings] = useState({
    tone: 'friendly',
    accent: 'american',
    pitch: 50,
    speed: 50,
    language: 'english',
    emotion: 'neutral',
    warmth: 50,
    clarity: 50
  });
  const [moodSettings, setMoodSettings] = useState({
    primary: 'playful',
    intensity: 70,
    stability: 50,
    openness: 60,
    responsiveness: 80,
    adaptability: 65
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [userCredits, setUserCredits] = useState({
    luvTokens: 240,
    desireGems: 120
  });
  const [shiftHistory, setShiftHistory] = useState<any[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [animationProgress, setAnimationProgress] = useState(0);
  const [completedShifts, setCompletedShifts] = useState(0);
  const [fullscreenPreview, setFullscreenPreview] = useState(false);
  const [isAIAssisted, setIsAIAssisted] = useState(false);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [activeVoiceSample, setActiveVoiceSample] = useState<string | null>(null);
  const [transformationIntensity, setTransformationIntensity] = useState(75);
  const [immersionLevel, setImmersionLevel] = useState(2); // 1-3 scale
  const animationRef = useRef<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  // Preset templates for each tab
  const personaPresets: ShiftPreset[] = [{
    id: 'gamer',
    name: 'Gamer Girl',
    description: 'Playful, competitive, and tech-savvy personality',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/ctdy7MKigXnXaDEzdjDUn3/image.png',
    cost: 1,
    isPremium: false,
    traits: {
      confidence: 70,
      playfulness: 90,
      intensity: 60,
      empathy: 40,
      creativity: 65,
      dominance: 45,
      sensuality: 40,
      intellect: 75
    },
    badge: 'Gamer'
  }, {
    id: 'ceo',
    name: 'CEO Powerhouse',
    description: 'Confident, assertive, and sophisticated executive',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tgcHoCQzuDkVPcyrZTSXpf/pasted-image.jpg',
    cost: 1,
    isPremium: false,
    traits: {
      confidence: 95,
      playfulness: 30,
      intensity: 80,
      empathy: 50,
      creativity: 60,
      dominance: 90,
      sensuality: 45,
      intellect: 85
    },
    badge: 'Boss'
  }, {
    id: 'cosplay',
    name: 'Cosplay Queen',
    description: 'Creative, expressive, and character-driven personality',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jUM8PJj8HrEVgmZS241EZW/pasted-image.jpg',
    cost: 3,
    isPremium: true,
    traits: {
      confidence: 75,
      playfulness: 85,
      intensity: 70,
      empathy: 60,
      creativity: 95,
      dominance: 40,
      sensuality: 65,
      intellect: 70
    },
    badge: 'Cosplay',
    effect: 'sparkle'
  }, {
    id: 'gothic',
    name: 'Gothic Muse',
    description: 'Mysterious, deep, and romantically dark personality',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/1Vbokr7VVR3UC2CvPLBWn8/image.png',
    cost: 3,
    isPremium: true,
    traits: {
      confidence: 65,
      playfulness: 30,
      intensity: 90,
      empathy: 70,
      creativity: 85,
      dominance: 60,
      sensuality: 75,
      intellect: 80
    },
    badge: 'Gothic',
    effect: 'shadow'
  }, {
    id: 'scholar',
    name: 'Intellectual Scholar',
    description: 'Thoughtful, analytical, and deeply knowledgeable',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/yFyRUmVZQwq4MsrA33eDaH/scholar.jpg',
    cost: 3,
    isPremium: true,
    traits: {
      confidence: 70,
      playfulness: 40,
      intensity: 60,
      empathy: 75,
      creativity: 70,
      dominance: 50,
      sensuality: 30,
      intellect: 95
    },
    badge: 'Scholar',
    effect: 'glow'
  }, {
    id: 'seductress',
    name: 'Seductive Temptress',
    description: 'Alluring, mysterious, and intensely passionate',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8qnxKBLrP1vKY9zXVvqkTB/seductress.jpg',
    cost: 5,
    isPremium: true,
    traits: {
      confidence: 90,
      playfulness: 60,
      intensity: 95,
      empathy: 40,
      creativity: 70,
      dominance: 85,
      sensuality: 100,
      intellect: 65
    },
    badge: 'Seductress',
    effect: 'pulse'
  }, {
    id: 'artist',
    name: 'Creative Artist',
    description: 'Expressive, emotional, and aesthetically driven',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/oSALRSNfRhWgDZN9tgH9Av/artist.jpg',
    cost: 2,
    isPremium: false,
    traits: {
      confidence: 60,
      playfulness: 70,
      intensity: 65,
      empathy: 80,
      creativity: 100,
      dominance: 30,
      sensuality: 60,
      intellect: 75
    },
    badge: 'Artist'
  }, {
    id: 'athlete',
    name: 'Fitness Enthusiast',
    description: 'Energetic, disciplined, and physically confident',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/vdXMKvRQJdNz8bFwXiNBiw/athlete.jpg',
    cost: 2,
    isPremium: false,
    traits: {
      confidence: 85,
      playfulness: 65,
      intensity: 80,
      empathy: 50,
      creativity: 40,
      dominance: 70,
      sensuality: 75,
      intellect: 55
    },
    badge: 'Athlete'
  }];
  const scenePresets: ShiftPreset[] = [{
    id: 'beach',
    name: 'Sunset Beach',
    description: 'Relaxing beach scene with golden sunset',
    image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2573&auto=format&fit=crop',
    cost: 1,
    isPremium: false,
    sceneProps: {
      outfit: 'swimwear',
      background: 'beach',
      lighting: 'golden',
      mood: 'relaxed',
      time: 'sunset',
      weather: 'clear',
      atmosphere: 'tranquil'
    }
  }, {
    id: 'luxury',
    name: 'Luxury Penthouse',
    description: 'High-end modern apartment with city views',
    image: 'https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?q=80&w=2670&auto=format&fit=crop',
    cost: 1,
    isPremium: false,
    sceneProps: {
      outfit: 'elegant',
      background: 'penthouse',
      lighting: 'evening',
      mood: 'sophisticated',
      time: 'night',
      weather: 'clear',
      atmosphere: 'luxurious'
    }
  }, {
    id: 'fantasy',
    name: 'Fantasy Realm',
    description: 'Magical forest with ethereal lighting',
    image: 'https://images.unsplash.com/photo-1518459031867-a89b944bffe4?q=80&w=2574&auto=format&fit=crop',
    cost: 3,
    isPremium: true,
    sceneProps: {
      outfit: 'fantasy',
      background: 'enchanted_forest',
      lighting: 'magical',
      mood: 'mysterious',
      time: 'twilight',
      weather: 'misty',
      atmosphere: 'enchanted'
    },
    effect: 'magical'
  }, {
    id: 'cyberpunk',
    name: 'Cyberpunk City',
    description: 'Futuristic neon-lit urban environment',
    image: 'https://images.unsplash.com/photo-1541701494587-cb58502866ab?q=80&w=2670&auto=format&fit=crop',
    cost: 5,
    isPremium: true,
    sceneProps: {
      outfit: 'futuristic',
      background: 'neon_city',
      lighting: 'neon',
      mood: 'edgy',
      time: 'night',
      weather: 'rainy',
      atmosphere: 'dystopian'
    },
    effect: 'neon'
  }, {
    id: 'office',
    name: 'Executive Office',
    description: 'Professional corporate environment with city skyline',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/uRJGRDFHYfTvLHJJdtPY2r/office.jpg',
    cost: 2,
    isPremium: false,
    sceneProps: {
      outfit: 'business',
      background: 'office',
      lighting: 'bright',
      mood: 'professional',
      time: 'day',
      weather: 'clear',
      atmosphere: 'productive'
    }
  }, {
    id: 'nightclub',
    name: 'VIP Nightclub',
    description: 'Exclusive club with dynamic lighting and music',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3LDTxfgQkzDfuEUQJmNzLa/nightclub.jpg',
    cost: 3,
    isPremium: true,
    sceneProps: {
      outfit: 'party',
      background: 'nightclub',
      lighting: 'dynamic',
      mood: 'energetic',
      time: 'night',
      weather: 'indoor',
      atmosphere: 'vibrant'
    },
    effect: 'flash'
  }, {
    id: 'vr_lounge',
    name: 'VR Lounge',
    description: 'Futuristic virtual reality environment',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qXjmW3KAKnrXYJMGzCyYSw/vr_lounge.jpg',
    cost: 4,
    isPremium: true,
    sceneProps: {
      outfit: 'tech',
      background: 'vr_space',
      lighting: 'digital',
      mood: 'immersive',
      time: 'timeless',
      weather: 'simulated',
      atmosphere: 'futuristic'
    },
    effect: 'digital'
  }, {
    id: 'spa',
    name: 'Luxury Spa',
    description: 'Serene wellness retreat with calming ambiance',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pGpWYhz1Eo9hxFGtZ2WrWQ/spa.jpg',
    cost: 2,
    isPremium: false,
    sceneProps: {
      outfit: 'relaxed',
      background: 'spa',
      lighting: 'soft',
      mood: 'serene',
      time: 'day',
      weather: 'indoor',
      atmosphere: 'peaceful'
    }
  }];
  const voicePresets: ShiftPreset[] = [{
    id: 'sweet',
    name: 'Sweet & Gentle',
    description: 'Soft, warm, and inviting voice tone',
    image: 'https://images.unsplash.com/photo-1516914589923-f105f1535f88?q=80&w=2574&auto=format&fit=crop',
    cost: 1,
    isPremium: false,
    voiceProps: {
      tone: 'gentle',
      accent: 'american',
      pitch: 65,
      speed: 45,
      language: 'english',
      emotion: 'caring',
      warmth: 80,
      clarity: 70
    }
  }, {
    id: 'confident',
    name: 'Confident & Clear',
    description: 'Strong, articulate, and authoritative voice',
    image: 'https://images.unsplash.com/photo-1571442463800-1337d7af9d2f?q=80&w=2670&auto=format&fit=crop',
    cost: 1,
    isPremium: false,
    voiceProps: {
      tone: 'confident',
      accent: 'british',
      pitch: 45,
      speed: 55,
      language: 'english',
      emotion: 'assertive',
      warmth: 40,
      clarity: 90
    }
  }, {
    id: 'asmr',
    name: 'ASMR Whisper',
    description: 'Intimate, close, and tingling whisper voice',
    image: 'https://images.unsplash.com/photo-1590698933947-a202b069a861?q=80&w=2574&auto=format&fit=crop',
    cost: 3,
    isPremium: true,
    voiceProps: {
      tone: 'whisper',
      accent: 'neutral',
      pitch: 60,
      speed: 35,
      language: 'english',
      emotion: 'intimate',
      warmth: 85,
      clarity: 60
    },
    effect: 'whisper'
  }, {
    id: 'multilingual',
    name: 'Multilingual Expert',
    description: 'Fluent in multiple languages with perfect accent',
    image: 'https://images.unsplash.com/photo-1516382799247-87df95d790b7?q=80&w=2574&auto=format&fit=crop',
    cost: 5,
    isPremium: true,
    voiceProps: {
      tone: 'natural',
      accent: 'international',
      pitch: 50,
      speed: 50,
      language: 'multi',
      emotion: 'neutral',
      warmth: 65,
      clarity: 85
    },
    badge: 'Polyglot'
  }, {
    id: 'sultry',
    name: 'Sultry & Seductive',
    description: 'Deep, alluring voice with passionate undertones',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qMQQmCQhCdMZuNwKZuQRJg/sultry.jpg',
    cost: 4,
    isPremium: true,
    voiceProps: {
      tone: 'seductive',
      accent: 'french',
      pitch: 40,
      speed: 40,
      language: 'english',
      emotion: 'passionate',
      warmth: 90,
      clarity: 75
    },
    effect: 'glow'
  }, {
    id: 'playful',
    name: 'Playful & Bubbly',
    description: 'Energetic, fun, and youthful voice',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9nNYHuZ4wvzuXjuBDJAuTu/playful.jpg',
    cost: 2,
    isPremium: false,
    voiceProps: {
      tone: 'playful',
      accent: 'american',
      pitch: 70,
      speed: 65,
      language: 'english',
      emotion: 'happy',
      warmth: 75,
      clarity: 80
    }
  }, {
    id: 'dominant',
    name: 'Dominant & Commanding',
    description: 'Authoritative voice with powerful presence',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/3vhwBbzUmBsWk7YXWZ2cqN/dominant.jpg',
    cost: 3,
    isPremium: true,
    voiceProps: {
      tone: 'commanding',
      accent: 'british',
      pitch: 35,
      speed: 45,
      language: 'english',
      emotion: 'intense',
      warmth: 30,
      clarity: 95
    },
    badge: 'Dominant'
  }, {
    id: 'soothing',
    name: 'Soothing & Calming',
    description: 'Relaxing voice that puts you at ease',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/eFbTWaAG4Jt6y1QNKYeKVi/soothing.jpg',
    cost: 2,
    isPremium: false,
    voiceProps: {
      tone: 'soothing',
      accent: 'neutral',
      pitch: 55,
      speed: 40,
      language: 'english',
      emotion: 'calm',
      warmth: 85,
      clarity: 75
    }
  }];
  const moodPresets: ShiftPreset[] = [{
    id: 'playful',
    name: 'Playful Tease',
    description: 'Flirty, fun, and light-hearted mood',
    image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=2562&auto=format&fit=crop',
    cost: 1,
    isPremium: false,
    traits: {
      confidence: 70,
      playfulness: 90,
      intensity: 50,
      empathy: 60,
      creativity: 75,
      dominance: 40,
      sensuality: 65,
      intellect: 55
    },
    sceneProps: {
      outfit: 'casual',
      background: 'bedroom',
      lighting: 'warm',
      mood: 'playful',
      time: 'day',
      weather: 'sunny',
      atmosphere: 'fun'
    },
    voiceProps: {
      tone: 'playful',
      accent: 'american',
      pitch: 60,
      speed: 55,
      language: 'english',
      emotion: 'happy',
      warmth: 75,
      clarity: 70
    },
    moodProps: {
      primary: 'playful',
      intensity: 80,
      stability: 60,
      openness: 75,
      responsiveness: 85,
      adaptability: 70
    }
  }, {
    id: 'intense',
    name: 'Intense Passion',
    description: 'Deep, passionate, and emotionally charged mood',
    image: 'https://images.unsplash.com/photo-1517263904808-5dc91e3e7044?q=80&w=2574&auto=format&fit=crop',
    cost: 3,
    isPremium: false,
    traits: {
      confidence: 85,
      playfulness: 40,
      intensity: 95,
      empathy: 70,
      creativity: 60,
      dominance: 75,
      sensuality: 90,
      intellect: 50
    },
    sceneProps: {
      outfit: 'intimate',
      background: 'dim_room',
      lighting: 'low',
      mood: 'passionate',
      time: 'night',
      weather: 'clear',
      atmosphere: 'intense'
    },
    voiceProps: {
      tone: 'intense',
      accent: 'neutral',
      pitch: 45,
      speed: 45,
      language: 'english',
      emotion: 'passionate',
      warmth: 80,
      clarity: 65
    },
    moodProps: {
      primary: 'passionate',
      intensity: 90,
      stability: 40,
      openness: 65,
      responsiveness: 80,
      adaptability: 50
    },
    effect: 'pulse'
  }, {
    id: 'dominate',
    name: 'Dominant Power',
    description: 'Commanding, controlling, and powerful presence',
    image: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=2564&auto=format&fit=crop',
    cost: 5,
    isPremium: true,
    traits: {
      confidence: 100,
      playfulness: 30,
      intensity: 90,
      empathy: 20,
      creativity: 70,
      dominance: 100,
      sensuality: 80,
      intellect: 75
    },
    sceneProps: {
      outfit: 'powerful',
      background: 'dark_room',
      lighting: 'dramatic',
      mood: 'dominant',
      time: 'night',
      weather: 'stormy',
      atmosphere: 'intense'
    },
    voiceProps: {
      tone: 'commanding',
      accent: 'british',
      pitch: 40,
      speed: 50,
      language: 'english',
      emotion: 'powerful',
      warmth: 30,
      clarity: 90
    },
    moodProps: {
      primary: 'dominant',
      intensity: 95,
      stability: 80,
      openness: 30,
      responsiveness: 60,
      adaptability: 40
    },
    badge: 'Dominant',
    effect: 'shadow'
  }, {
    id: 'fantasy',
    name: 'Fantasy Role',
    description: 'Immersive character role with complete transformation',
    image: 'https://images.unsplash.com/photo-1578632767115-351597cf2477?q=80&w=2574&auto=format&fit=crop',
    cost: 5,
    isPremium: true,
    traits: {
      confidence: 80,
      playfulness: 70,
      intensity: 75,
      empathy: 60,
      creativity: 100,
      dominance: 65,
      sensuality: 80,
      intellect: 75
    },
    sceneProps: {
      outfit: 'costume',
      background: 'fantasy',
      lighting: 'theatrical',
      mood: 'roleplay',
      time: 'timeless',
      weather: 'magical',
      atmosphere: 'immersive'
    },
    voiceProps: {
      tone: 'character',
      accent: 'stylized',
      pitch: 55,
      speed: 50,
      language: 'english',
      emotion: 'expressive',
      warmth: 70,
      clarity: 75
    },
    moodProps: {
      primary: 'fantasy',
      intensity: 85,
      stability: 60,
      openness: 90,
      responsiveness: 75,
      adaptability: 85
    },
    badge: 'Fantasy',
    effect: 'magical'
  }, {
    id: 'shy',
    name: 'Shy & Innocent',
    description: 'Sweet, bashful, and endearingly innocent',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gQVvbRE9vUPCfwR9JpHpXX/shy.jpg',
    cost: 2,
    isPremium: false,
    traits: {
      confidence: 30,
      playfulness: 60,
      intensity: 40,
      empathy: 85,
      creativity: 65,
      dominance: 20,
      sensuality: 45,
      intellect: 70
    },
    sceneProps: {
      outfit: 'cute',
      background: 'cozy_room',
      lighting: 'soft',
      mood: 'shy',
      time: 'morning',
      weather: 'sunny',
      atmosphere: 'warm'
    },
    voiceProps: {
      tone: 'soft',
      accent: 'neutral',
      pitch: 70,
      speed: 45,
      language: 'english',
      emotion: 'shy',
      warmth: 75,
      clarity: 65
    },
    moodProps: {
      primary: 'shy',
      intensity: 75,
      stability: 65,
      openness: 50,
      responsiveness: 60,
      adaptability: 55
    }
  }, {
    id: 'cold',
    name: 'Cold & Distant',
    description: 'Aloof, mysterious, and emotionally reserved',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/qnuPFBLRc9hFKAQWEUuFNv/cold.jpg',
    cost: 3,
    isPremium: true,
    traits: {
      confidence: 75,
      playfulness: 20,
      intensity: 60,
      empathy: 30,
      creativity: 50,
      dominance: 70,
      sensuality: 40,
      intellect: 90
    },
    sceneProps: {
      outfit: 'elegant',
      background: 'minimalist',
      lighting: 'cool',
      mood: 'distant',
      time: 'night',
      weather: 'snowy',
      atmosphere: 'cold'
    },
    voiceProps: {
      tone: 'distant',
      accent: 'neutral',
      pitch: 40,
      speed: 40,
      language: 'english',
      emotion: 'detached',
      warmth: 20,
      clarity: 85
    },
    moodProps: {
      primary: 'cold',
      intensity: 80,
      stability: 90,
      openness: 20,
      responsiveness: 30,
      adaptability: 40
    },
    effect: 'frost'
  }, {
    id: 'romantic',
    name: 'Romantic & Tender',
    description: 'Loving, intimate, and emotionally connected',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wdTKEbvn3UqQFz4Wt3Rw8r/romantic.jpg',
    cost: 3,
    isPremium: false,
    traits: {
      confidence: 65,
      playfulness: 55,
      intensity: 70,
      empathy: 90,
      creativity: 75,
      dominance: 45,
      sensuality: 80,
      intellect: 65
    },
    sceneProps: {
      outfit: 'elegant',
      background: 'romantic',
      lighting: 'candlelight',
      mood: 'intimate',
      time: 'evening',
      weather: 'clear',
      atmosphere: 'romantic'
    },
    voiceProps: {
      tone: 'tender',
      accent: 'french',
      pitch: 55,
      speed: 40,
      language: 'english',
      emotion: 'loving',
      warmth: 90,
      clarity: 75
    },
    moodProps: {
      primary: 'romantic',
      intensity: 85,
      stability: 75,
      openness: 80,
      responsiveness: 90,
      adaptability: 70
    },
    effect: 'glow'
  }, {
    id: 'teasing',
    name: 'Teasing & Coy',
    description: 'Playfully provocative and tantalizingly elusive',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/sNLyHW4BVKmkGfDEAyNr1A/teasing.jpg',
    cost: 4,
    isPremium: true,
    traits: {
      confidence: 80,
      playfulness: 85,
      intensity: 65,
      empathy: 50,
      creativity: 80,
      dominance: 60,
      sensuality: 85,
      intellect: 70
    },
    sceneProps: {
      outfit: 'flirty',
      background: 'bedroom',
      lighting: 'soft',
      mood: 'playful',
      time: 'evening',
      weather: 'clear',
      atmosphere: 'intimate'
    },
    voiceProps: {
      tone: 'teasing',
      accent: 'american',
      pitch: 65,
      speed: 45,
      language: 'english',
      emotion: 'playful',
      warmth: 75,
      clarity: 80
    },
    moodProps: {
      primary: 'teasing',
      intensity: 80,
      stability: 60,
      openness: 70,
      responsiveness: 85,
      adaptability: 75
    },
    badge: 'Tease'
  }];
  // Get active presets based on current tab
  const getActivePresets = () => {
    switch (activeTab) {
      case 'persona':
        return personaPresets;
      case 'scene':
        return scenePresets;
      case 'voice':
        return voicePresets;
      case 'mood':
        return moodPresets;
      default:
        return personaPresets;
    }
  };
  // Calculate the cost of the current shift
  const calculateShiftCost = () => {
    if (selectedPreset) {
      return selectedPreset.cost;
    }
    // Calculate based on tab and customization level
    let baseCost = 1;
    switch (activeTab) {
      case 'persona':
        baseCost = 2;
        break;
      case 'scene':
        baseCost = 2;
        break;
      case 'voice':
        baseCost = voiceSettings.language !== 'english' ? 3 : 2;
        break;
      case 'mood':
        baseCost = 3;
        break;
      default:
        baseCost = 2;
    }
    // Add cost for high immersion level
    if (immersionLevel === 3) {
      baseCost += 2;
    } else if (immersionLevel === 2) {
      baseCost += 1;
    }
    // Add cost for AI assistance
    if (isAIAssisted) {
      baseCost += 1;
    }
    return baseCost;
  };
  // Handle preset selection
  const handlePresetSelect = (preset: ShiftPreset) => {
    setSelectedPreset(preset);
    // Apply preset settings based on type
    if (preset.traits) {
      setCustomTraits({
        ...customTraits,
        ...preset.traits
      });
    }
    if (preset.sceneProps) {
      setSceneSettings({
        ...sceneSettings,
        ...preset.sceneProps
      });
    }
    if (preset.voiceProps) {
      setVoiceSettings({
        ...voiceSettings,
        ...preset.voiceProps
      });
    }
    if (preset.moodProps) {
      setMoodSettings({
        ...moodSettings,
        ...preset.moodProps
      });
    }
  };
  // Handle trait slider changes
  const handleTraitChange = (trait: string, value: number) => {
    setCustomTraits(prev => ({
      ...prev,
      [trait]: value
    }));
  };
  // Handle scene setting changes
  const handleSceneChange = (setting: string, value: string) => {
    setSceneSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  // Handle voice setting changes
  const handleVoiceChange = (setting: string, value: any) => {
    setVoiceSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  // Handle mood setting changes
  const handleMoodChange = (setting: string, value: any) => {
    setMoodSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };
  // Handle AI-assisted customization
  const handleAIAssist = () => {
    setIsAIAssisted(true);
    setIsProcessing(true);
    // Simulate AI processing
    setTimeout(() => {
      // Generate AI-suggested values based on prompt
      if (prompt) {
        const lowerPrompt = prompt.toLowerCase();
        // Simple keyword matching for demo purposes
        // In a real implementation, this would use a more sophisticated AI model
        let newTraits = {
          ...customTraits
        };
        let newScene = {
          ...sceneSettings
        };
        let newVoice = {
          ...voiceSettings
        };
        let newMood = {
          ...moodSettings
        };
        // Confidence
        if (lowerPrompt.includes('confident') || lowerPrompt.includes('strong')) {
          newTraits.confidence = Math.min(newTraits.confidence + 30, 100);
        } else if (lowerPrompt.includes('shy') || lowerPrompt.includes('timid')) {
          newTraits.confidence = Math.max(newTraits.confidence - 30, 0);
        }
        // Playfulness
        if (lowerPrompt.includes('playful') || lowerPrompt.includes('fun') || lowerPrompt.includes('teasing')) {
          newTraits.playfulness = Math.min(newTraits.playfulness + 30, 100);
        } else if (lowerPrompt.includes('serious') || lowerPrompt.includes('stern')) {
          newTraits.playfulness = Math.max(newTraits.playfulness - 30, 0);
        }
        // Intensity
        if (lowerPrompt.includes('intense') || lowerPrompt.includes('passionate')) {
          newTraits.intensity = Math.min(newTraits.intensity + 30, 100);
        } else if (lowerPrompt.includes('calm') || lowerPrompt.includes('gentle')) {
          newTraits.intensity = Math.max(newTraits.intensity - 30, 0);
        }
        // Dominance
        if (lowerPrompt.includes('dominant') || lowerPrompt.includes('controlling')) {
          newTraits.dominance = Math.min(newTraits.dominance + 40, 100);
        } else if (lowerPrompt.includes('submissive') || lowerPrompt.includes('obedient')) {
          newTraits.dominance = Math.max(newTraits.dominance - 40, 0);
        }
        // Sensuality
        if (lowerPrompt.includes('sensual') || lowerPrompt.includes('seductive') || lowerPrompt.includes('sexy')) {
          newTraits.sensuality = Math.min(newTraits.sensuality + 35, 100);
        } else if (lowerPrompt.includes('modest') || lowerPrompt.includes('reserved')) {
          newTraits.sensuality = Math.max(newTraits.sensuality - 35, 0);
        }
        // Scene settings
        if (lowerPrompt.includes('night') || lowerPrompt.includes('dark')) {
          newScene.time = 'night';
          newScene.lighting = 'low';
        } else if (lowerPrompt.includes('day') || lowerPrompt.includes('bright')) {
          newScene.time = 'day';
          newScene.lighting = 'bright';
        }
        if (lowerPrompt.includes('beach') || lowerPrompt.includes('ocean')) {
          newScene.background = 'beach';
          newScene.outfit = 'swimwear';
        } else if (lowerPrompt.includes('office') || lowerPrompt.includes('professional')) {
          newScene.background = 'office';
          newScene.outfit = 'business';
        } else if (lowerPrompt.includes('bedroom') || lowerPrompt.includes('intimate')) {
          newScene.background = 'bedroom';
          newScene.outfit = 'intimate';
        }
        // Voice settings
        if (lowerPrompt.includes('soft') || lowerPrompt.includes('gentle')) {
          newVoice.tone = 'gentle';
          newVoice.pitch = 65;
          newVoice.speed = 45;
        } else if (lowerPrompt.includes('commanding') || lowerPrompt.includes('authoritative')) {
          newVoice.tone = 'commanding';
          newVoice.pitch = 40;
          newVoice.speed = 50;
        } else if (lowerPrompt.includes('whisper') || lowerPrompt.includes('asmr')) {
          newVoice.tone = 'whisper';
          newVoice.pitch = 60;
          newVoice.speed = 35;
        }
        // Mood settings
        if (lowerPrompt.includes('playful') || lowerPrompt.includes('teasing')) {
          newMood.primary = 'playful';
          newMood.intensity = 80;
        } else if (lowerPrompt.includes('passionate') || lowerPrompt.includes('intense')) {
          newMood.primary = 'passionate';
          newMood.intensity = 90;
        } else if (lowerPrompt.includes('dominant') || lowerPrompt.includes('controlling')) {
          newMood.primary = 'dominant';
          newMood.intensity = 95;
        } else if (lowerPrompt.includes('shy') || lowerPrompt.includes('innocent')) {
          newMood.primary = 'shy';
          newMood.intensity = 75;
        }
        // Update states with AI-suggested values
        setCustomTraits(newTraits);
        setSceneSettings(newScene);
        setVoiceSettings(newVoice);
        setMoodSettings(newMood);
      }
      setIsProcessing(false);
      // Show notification about AI assistance
      const event = new CustomEvent('showNotification', {
        detail: {
          title: 'AI Assistance',
          message: 'Your settings have been optimized based on your prompt!',
          type: 'success'
        }
      });
      document.dispatchEvent(event);
    }, 2000);
  };
  // Handle apply button click
  const handleApply = () => {
    const cost = calculateShiftCost();
    // Check if user has enough credits
    if (userCredits.desireGems < cost && userCredits.luvTokens < cost * 10) {
      // Show insufficient credits modal or notification
      const event = new CustomEvent('showNotification', {
        detail: {
          title: 'Insufficient Credits',
          message: `You need ${cost} DesireGems or ${cost * 10} LuvTokens to apply this ShapeShift.`,
          type: 'error'
        }
      });
      document.dispatchEvent(event);
      return;
    }
    // Start processing animation
    setIsProcessing(true);
    // Simulate processing delay (would be a real API call in production)
    setTimeout(() => {
      setIsProcessing(false);
      setIsPreviewMode(true);
      startPreviewAnimation();
      // Start animation progress
      setAnimationProgress(0);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      let startTime: number | null = null;
      const duration = 3000; // 3 seconds for the animation
      const animate = (timestamp: number) => {
        if (!startTime) startTime = timestamp;
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setAnimationProgress(progress * 100);
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        } else {
          // Animation complete
          setShowConfirmation(true);
        }
      };
      animationRef.current = requestAnimationFrame(animate);
    }, 1500);
  };
  // Start preview animation
  const startPreviewAnimation = () => {
    // This would be replaced with actual animation logic
    console.log('Starting preview animation');
  };
  // Handle confirmation (apply the shift)
  const handleConfirm = () => {
    const cost = calculateShiftCost();
    // Deduct credits
    if (userCredits.desireGems >= cost) {
      setUserCredits(prev => ({
        ...prev,
        desireGems: prev.desireGems - cost
      }));
    } else {
      setUserCredits(prev => ({
        ...prev,
        luvTokens: prev.luvTokens - cost * 10
      }));
    }
    // Add to history
    const newShift = {
      id: Date.now().toString(),
      type: activeTab,
      preset: selectedPreset?.name || 'Custom',
      timestamp: new Date().toISOString(),
      cost
    };
    setShiftHistory([newShift, ...shiftHistory]);
    // Increment completed shifts
    setCompletedShifts(prev => prev + 1);
    // Reset UI state
    setIsPreviewMode(false);
    setShowConfirmation(false);
    // Check if this is for a ritual completion
    if (ritualId) {
      // Dispatch ritual completion event
      const completeEvent = new CustomEvent('ritualCompleted', {
        detail: {
          ritualId,
          type: 'shape_shift',
          subtype: activeTab
        }
      });
      document.dispatchEvent(completeEvent);
    }
    // If in playroom, notify other users
    if (inPlayroom) {
      const playroomEvent = new CustomEvent('playroomShapeShift', {
        detail: {
          angelId,
          angelName,
          shiftType: activeTab,
          presetName: selectedPreset?.name || 'Custom',
          badge: selectedPreset?.badge,
          effect: selectedPreset?.effect
        }
      });
      document.dispatchEvent(playroomEvent);
    }
    // Notify user of successful shift
    const event = new CustomEvent('showNotification', {
      detail: {
        title: 'ShapeShift Applied',
        message: `${angelName} has been transformed with the ${selectedPreset?.name || 'custom'} ${activeTab} shift!`,
        type: 'success'
      }
    });
    document.dispatchEvent(event);
    // Call onComplete callback if provided
    if (onComplete) {
      onComplete({
        type: activeTab,
        preset: selectedPreset?.name || 'Custom',
        traits: customTraits,
        scene: sceneSettings,
        voice: voiceSettings,
        mood: moodSettings,
        badge: selectedPreset?.badge,
        effect: selectedPreset?.effect,
        immersionLevel
      });
    }
    // Check for achievement unlocks
    checkAchievements();
    // Dispatch ShapeShift complete event
    const shapeShiftEvent = new CustomEvent('shapeShiftComplete', {
      detail: {
        type: activeTab,
        preset: selectedPreset?.name || 'Custom',
        ritualId,
        result: {
          type: activeTab,
          preset: selectedPreset?.name || 'Custom',
          badge: selectedPreset?.badge,
          effect: selectedPreset?.effect
        }
      }
    });
    document.dispatchEvent(shapeShiftEvent);
  };
  // Check for achievements
  const checkAchievements = () => {
    const totalShifts = completedShifts + 1;
    if (totalShifts === 10) {
      // Unlock "Shapeshifter" achievement
      const achievementEvent = new CustomEvent('unlockAchievement', {
        detail: {
          id: 'shapeshifter',
          name: 'Shapeshifter',
          description: 'Complete 10 persona changes'
        }
      });
      document.dispatchEvent(achievementEvent);
    }
    if (totalShifts === 50) {
      // Unlock "Master of Masks" achievement
      const achievementEvent = new CustomEvent('unlockAchievement', {
        detail: {
          id: 'master_of_masks',
          name: 'Master of Masks',
          description: 'Complete 50 persona changes'
        }
      });
      document.dispatchEvent(achievementEvent);
    }
  };
  // Handle cancel button click
  const handleCancel = () => {
    if (isPreviewMode) {
      setIsPreviewMode(false);
      setShowConfirmation(false);
      // Cancel animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    } else {
      onClose();
    }
  };
  // Toggle voice preview
  const toggleVoicePreview = (sampleId?: string) => {
    if (isPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        setActiveVoiceSample(null);
      }
    } else {
      // In a real implementation, this would generate or play the actual voice sample
      // For now, we'll use sample audio files
      const audioSamples: Record<string, string> = {
        sweet: 'https://cdn.freesound.org/previews/531/531509_11861866-lq.mp3',
        confident: 'https://cdn.freesound.org/previews/322/322102_5823427-lq.mp3',
        asmr: 'https://cdn.freesound.org/previews/346/346176_5823427-lq.mp3',
        sultry: 'https://cdn.freesound.org/previews/412/412016_5823427-lq.mp3',
        playful: 'https://cdn.freesound.org/previews/277/277021_5823427-lq.mp3',
        dominant: 'https://cdn.freesound.org/previews/412/412068_5823427-lq.mp3',
        soothing: 'https://cdn.freesound.org/previews/346/346177_5823427-lq.mp3',
        default: 'https://cdn.freesound.org/previews/531/531509_11861866-lq.mp3'
      };
      const sampleToPlay = sampleId || 'default';
      const audioUrl = audioSamples[sampleToPlay] || audioSamples.default;
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(audioUrl);
      audioRef.current.play();
      setIsPlaying(true);
      setActiveVoiceSample(sampleId || null);
    }
  };
  // Toggle fullscreen preview
  const toggleFullscreenPreview = () => {
    setFullscreenPreview(!fullscreenPreview);
    if (!fullscreenPreview && previewRef.current) {
      if (previewRef.current.requestFullscreen) {
        previewRef.current.requestFullscreen();
      }
    }
  };
  // Handle random shuffle of settings
  const handleRandomize = () => {
    // Randomly select settings for the current tab
    switch (activeTab) {
      case 'persona':
        const randomTraits = Object.keys(customTraits).reduce((acc, trait) => {
          acc[trait] = Math.floor(Math.random() * 100);
          return acc;
        }, {} as Record<string, number>);
        setCustomTraits(randomTraits as any);
        break;
      case 'scene':
        const backgrounds = ['bedroom', 'beach', 'office', 'nightclub', 'vr_space', 'spa', 'penthouse', 'enchanted_forest'];
        const outfits = ['casual', 'elegant', 'intimate', 'business', 'party', 'swimwear', 'fantasy', 'relaxed'];
        const lighting = ['soft', 'bright', 'low', 'golden', 'neon', 'dramatic', 'magical', 'candlelight'];
        const times = ['day', 'night', 'sunset', 'morning', 'evening', 'twilight', 'timeless'];
        const weathers = ['clear', 'rainy', 'snowy', 'misty', 'sunny', 'stormy', 'magical'];
        setSceneSettings({
          background: backgrounds[Math.floor(Math.random() * backgrounds.length)],
          outfit: outfits[Math.floor(Math.random() * outfits.length)],
          lighting: lighting[Math.floor(Math.random() * lighting.length)],
          mood: 'relaxed',
          time: times[Math.floor(Math.random() * times.length)],
          weather: weathers[Math.floor(Math.random() * weathers.length)],
          atmosphere: 'cozy' // Keep atmosphere stable
        });
        break;
      case 'voice':
        const tones = ['gentle', 'confident', 'whisper', 'seductive', 'playful', 'commanding', 'soothing'];
        const accents = ['american', 'british', 'french', 'neutral', 'international'];
        const emotions = ['caring', 'assertive', 'intimate', 'passionate', 'happy', 'intense', 'calm'];
        setVoiceSettings({
          tone: tones[Math.floor(Math.random() * tones.length)],
          accent: accents[Math.floor(Math.random() * accents.length)],
          pitch: Math.floor(Math.random() * 100),
          speed: Math.floor(Math.random() * 100),
          language: 'english',
          emotion: emotions[Math.floor(Math.random() * emotions.length)],
          warmth: Math.floor(Math.random() * 100),
          clarity: Math.floor(Math.random() * 100)
        });
        break;
      case 'mood':
        const moods = ['playful', 'passionate', 'dominant', 'shy', 'cold', 'romantic', 'teasing'];
        setMoodSettings({
          primary: moods[Math.floor(Math.random() * moods.length)],
          intensity: Math.floor(Math.random() * 100),
          stability: Math.floor(Math.random() * 100),
          openness: Math.floor(Math.random() * 100),
          responsiveness: Math.floor(Math.random() * 100),
          adaptability: Math.floor(Math.random() * 100)
        });
        break;
    }
    // Show notification
    const event = new CustomEvent('showNotification', {
      detail: {
        title: 'Settings Randomized',
        message: `Your ${activeTab} settings have been randomly generated!`,
        type: 'info'
      }
    });
    document.dispatchEvent(event);
  };
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);
  // Update document title
  useEffect(() => {
    if (isOpen) {
      document.title = 'BlackMirror ShapeShift - SinShift';
      return () => {
        document.title = 'SinShift';
      };
    }
  }, [isOpen]);
  // Handle escape key for fullscreen
  useEffect(() => {
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && fullscreenPreview) {
        setFullscreenPreview(false);
      }
    };
    document.addEventListener('keydown', handleEscKey);
    document.addEventListener('fullscreenchange', () => {
      if (!document.fullscreenElement) {
        setFullscreenPreview(false);
      }
    });
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.removeEventListener('fullscreenchange', () => {});
    };
  }, [fullscreenPreview]);
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in duration-300" data-id="element-19144">
      <div className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-black border border-purple-500/30 shadow-xl shadow-purple-500/20 animate-in zoom-in-95 duration-300" data-id="element-19145">
        {/* Holographic effects */}
        <div className="absolute inset-0 bg-gradient-to-tr from-purple-500/10 via-cyan-500/5 to-pink-500/10 pointer-events-none" data-id="element-19146"></div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-19147">
          <div className="absolute -inset-[10px] opacity-30 blur-xl bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 animate-pulse-slow" data-id="element-19148"></div>
          <div className="absolute top-0 left-1/4 w-1/2 h-px bg-gradient-to-r from-transparent via-purple-500 to-transparent" data-id="element-19149"></div>
          <div className="absolute bottom-0 left-1/3 w-1/3 h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent" data-id="element-19150"></div>
        </div>
        {/* Close button */}
        <button onClick={onClose} className="absolute right-4 top-4 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" aria-label="Close" data-id="element-19151">
          <X size={18} data-id="element-19152" />
        </button>
        {/* Header */}
        <div className="relative p-6 border-b border-white/10" data-id="element-19153">
          <div className="flex items-center gap-3" data-id="element-19154">
            <div className="w-10 h-10 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 shadow-lg shadow-purple-600/20" data-id="element-19155">
              <RefreshCw size={20} className="text-white" data-id="element-19156" />
            </div>
            <div data-id="element-19157">
              <h2 className="text-xl font-bold text-white flex items-center gap-2" data-id="element-19158">
                BlackMirror ShapeShift
                <span className="text-xs px-2 py-0.5 bg-purple-500/30 rounded-full border border-purple-500/50" data-id="element-19159">
                  AI
                </span>
              </h2>
              <p className="text-gray-300 text-sm" data-id="element-19160">
                Immersive AI persona transformation system
              </p>
            </div>
          </div>
        </div>
        {/* Content area */}
        <div className="relative flex flex-col h-[calc(90vh-150px)]" data-id="element-19161">
          {/* Tabs */}
          <div className="flex border-b border-white/10" data-id="element-19162">
            {(['persona', 'scene', 'voice', 'mood'] as ShiftTab[]).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors ${activeTab === tab ? 'text-white bg-gradient-to-r from-purple-600/20 to-pink-600/20 border-b-2 border-purple-500' : 'text-gray-400 hover:text-white hover:bg-white/5'}`} data-id="element-19163">
                  {tab === 'persona' && <PersonStanding size={16} data-id="element-19164" />}
                  {tab === 'scene' && <Image size={16} data-id="element-19165" />}
                  {tab === 'voice' && <Mic size={16} data-id="element-19166" />}
                  {tab === 'mood' && <CloudLightning size={16} data-id="element-19167" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)} Shift
                </button>)}
          </div>
          {/* Main content */}
          <div className="flex flex-col md:flex-row flex-1 overflow-hidden" data-id="element-19168">
            {/* Left side - Presets */}
            <div className="w-full md:w-1/3 p-4 overflow-y-auto border-r border-white/10" data-id="element-19169">
              <h3 className="text-white font-medium mb-3" data-id="element-19170">Preset Templates</h3>
              <div className="space-y-3" data-id="element-19171">
                {getActivePresets().map(preset => <div key={preset.id} onClick={() => handlePresetSelect(preset)} className={`relative rounded-lg overflow-hidden cursor-pointer transition-all ${selectedPreset?.id === preset.id ? 'ring-2 ring-purple-500 scale-[1.02]' : 'hover:ring-1 hover:ring-purple-500/50'}`} data-id="element-19172">
                    {/* Preset image */}
                    <div className="relative h-32" data-id="element-19173">
                      <img src={preset.image} alt={preset.name} className="w-full h-full object-cover" data-id="element-19174" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" data-id="element-19175"></div>
                      {/* Premium badge */}
                      {preset.isPremium && <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-yellow-400 text-xs font-medium" data-id="element-19176">
                          <Sparkles size={12} data-id="element-19177" />
                          <span data-id="element-19178">Premium</span>
                        </div>}
                      {/* Badge */}
                      {preset.badge && <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-purple-400 text-xs font-medium" data-id="element-19179">
                          <BadgeCheck size={12} data-id="element-19180" />
                          <span data-id="element-19181">{preset.badge}</span>
                        </div>}
                      {/* Cost indicator */}
                      <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 text-white text-xs" data-id="element-19182">
                        <Gem size={12} className="text-purple-400" data-id="element-19183" />
                        <span data-id="element-19184">{preset.cost}</span>
                      </div>
                      {/* Name and description */}
                      <div className="absolute bottom-0 left-0 w-full p-3" data-id="element-19185">
                        <h4 className="text-white font-medium text-sm" data-id="element-19186">
                          {preset.name}
                        </h4>
                      </div>
                    </div>
                    {/* Description */}
                    <div className="p-2 bg-gray-900" data-id="element-19187">
                      <p className="text-gray-300 text-xs" data-id="element-19188">
                        {preset.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
            {/* Right side - Customization */}
            <div className="flex-1 p-4 overflow-y-auto" data-id="element-19189">
              {!isPreviewMode ? <>
                  {/* Prompt input with AI assistance */}
                  <div className="mb-6" data-id="element-19190">
                    <label className="block text-gray-300 text-sm font-medium mb-2 flex items-center justify-between" data-id="element-19191">
                      <span data-id="element-19192">Describe the transformation</span>
                      <button onClick={handleAIAssist} disabled={isProcessing || !prompt.trim()} className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs ${isProcessing || !prompt.trim() ? 'bg-gray-700 text-gray-400 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:shadow-sm hover:shadow-purple-500/20'}`} data-id="element-19193">
                        {isProcessing ? <>
                            <Loader size={10} className="animate-spin" data-id="element-19194" />
                            <span data-id="element-19195">Processing...</span>
                          </> : <>
                            <Wand2 size={10} data-id="element-19196" />
                            <span data-id="element-19197">AI Assist</span>
                          </>}
                      </button>
                    </label>
                    <div className="relative" data-id="element-19198">
                      <input type="text" value={prompt} onChange={e => setPrompt(e.target.value)} placeholder={`Describe how you want the ${activeTab} to change...`} className="w-full px-4 py-3 bg-black/50 border border-purple-500/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50" data-id="element-19199" />
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2" data-id="element-19200">
                        <Sparkles size={18} className="text-purple-400" data-id="element-19201" />
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-400 italic" data-id="element-19202">
                      Example: "Make her a confident and seductive goth with a
                      mysterious voice"
                    </p>
                  </div>
                  {/* BlackMirror Immersion Level Selector */}
                  <div className="mb-6 p-3 rounded-lg bg-black/30 border border-purple-500/20" data-id="element-19203">
                    <h3 className="text-white font-medium mb-2 flex items-center gap-1.5" data-id="element-19204">
                      <Brain size={16} className="text-purple-400" data-id="element-19205" />
                      <span data-id="element-19206">BlackMirror Immersion Level</span>
                    </h3>
                    <div className="flex gap-2 mb-3" data-id="element-19207">
                      <button onClick={() => setImmersionLevel(1)} className={`flex-1 py-2 rounded-lg text-sm font-medium ${immersionLevel === 1 ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19208">
                        Basic
                      </button>
                      <button onClick={() => setImmersionLevel(2)} className={`flex-1 py-2 rounded-lg text-sm font-medium ${immersionLevel === 2 ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19209">
                        Enhanced
                      </button>
                      <button onClick={() => setImmersionLevel(3)} className={`flex-1 py-2 rounded-lg text-sm font-medium ${immersionLevel === 3 ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19210">
                        Complete
                      </button>
                    </div>
                    <p className="text-xs text-gray-400" data-id="element-19211">
                      {immersionLevel === 1 && 'Basic transformation with minimal visual effects'}
                      {immersionLevel === 2 && 'Enhanced transformation with visual and behavioral changes'}
                      {immersionLevel === 3 && 'Complete transformation with full immersion and special effects'}
                    </p>
                  </div>
                  {/* Transformation Intensity Slider */}
                  <div className="mb-6" data-id="element-19212">
                    <div className="flex justify-between items-center mb-2" data-id="element-19213">
                      <label className="text-gray-300 text-sm font-medium flex items-center gap-1.5" data-id="element-19214">
                        <Sliders size={16} className="text-purple-400" data-id="element-19215" />
                        <span data-id="element-19216">Transformation Intensity</span>
                      </label>
                      <span className="text-purple-400 text-sm" data-id="element-19217">
                        {transformationIntensity}%
                      </span>
                    </div>
                    <input type="range" min="0" max="100" value={transformationIntensity} onChange={e => setTransformationIntensity(parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19218" />
                    <div className="flex justify-between text-xs text-gray-400 mt-1" data-id="element-19219">
                      <span data-id="element-19220">Subtle</span>
                      <span data-id="element-19221">Balanced</span>
                      <span data-id="element-19222">Dramatic</span>
                    </div>
                  </div>
                  {/* Advanced Settings Toggle */}
                  <div className="mb-4" data-id="element-19223">
                    <button onClick={() => setShowAdvancedSettings(!showAdvancedSettings)} className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-white" data-id="element-19224">
                      <Settings size={14} className="text-purple-400" data-id="element-19225" />
                      <span data-id="element-19226">Advanced Settings</span>
                      {showAdvancedSettings ? <ChevronRight size={14} className="transform rotate-90" data-id="element-19227" /> : <ChevronRight size={14} data-id="element-19228" />}
                    </button>
                  </div>
                  {/* Randomize Button */}
                  <div className="mb-6" data-id="element-19229">
                    <button onClick={handleRandomize} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm hover:shadow-sm hover:shadow-indigo-500/20 transition-all" data-id="element-19230">
                      <Shuffle size={14} data-id="element-19231" />
                      <span data-id="element-19232">
                        Randomize{' '}
                        {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}{' '}
                        Settings
                      </span>
                    </button>
                  </div>
                  {/* Tab-specific customization */}
                  {activeTab === 'persona' && <div className="space-y-6" data-id="element-19233">
                      <h3 className="text-white font-medium flex items-center gap-1.5" data-id="element-19234">
                        <PersonStanding size={16} className="text-purple-400" data-id="element-19235" />
                        <span data-id="element-19236">Personality Traits</span>
                      </h3>
                      {/* Sliders for personality traits */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4" data-id="element-19237">
                        {Object.entries(customTraits).map(([trait, value]) => <div key={trait} className="space-y-2" data-id="element-19238">
                            <div className="flex justify-between" data-id="element-19239">
                              <label className="text-gray-300 text-sm capitalize" data-id="element-19240">
                                {trait}
                              </label>
                              <span className="text-purple-400 text-sm" data-id="element-19241">
                                {value}%
                              </span>
                            </div>
                            <input type="range" min="0" max="100" value={value} onChange={e => handleTraitChange(trait, parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19242" />
                            <div className="flex justify-between text-xs text-gray-500" data-id="element-19243">
                              <span data-id="element-19244">Low</span>
                              <span data-id="element-19245">High</span>
                            </div>
                          </div>)}
                      </div>
                      {/* Persona Builder Explanation */}
                      {showAdvancedSettings && <div className="mt-4 p-3 rounded-lg bg-black/30 border border-purple-500/20" data-id="element-19246">
                          <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-1.5" data-id="element-19247">
                            <Lightbulb size={14} className="text-yellow-400" data-id="element-19248" />
                            <span data-id="element-19249">Custom Persona Builder</span>
                          </h4>
                          <p className="text-gray-300 text-xs" data-id="element-19250">
                            Adjust the sliders to create a unique personality
                            profile. Higher values increase that trait's
                            influence on the AI's behavior and responses.
                          </p>
                          <div className="mt-2 grid grid-cols-2 gap-2 text-xs" data-id="element-19251">
                            <div className="flex items-center gap-1" data-id="element-19252">
                              <Crown size={12} className="text-yellow-400" data-id="element-19253" />
                              <span className="text-gray-300" data-id="element-19254">
                                Confidence: Authority and self-assurance
                              </span>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-19255">
                              <Flame size={12} className="text-orange-400" data-id="element-19256" />
                              <span className="text-gray-300" data-id="element-19257">
                                Intensity: Emotional depth and passion
                              </span>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-19258">
                              <Heart size={12} className="text-pink-400" data-id="element-19259" />
                              <span className="text-gray-300" data-id="element-19260">
                                Empathy: Understanding and compassion
                              </span>
                            </div>
                            <div className="flex items-center gap-1" data-id="element-19261">
                              <Palette size={12} className="text-blue-400" data-id="element-19262" />
                              <span className="text-gray-300" data-id="element-19263">
                                Creativity: Imagination and expression
                              </span>
                            </div>
                          </div>
                        </div>}
                    </div>}
                  {activeTab === 'scene' && <div className="space-y-6" data-id="element-19264">
                      <h3 className="text-white font-medium flex items-center gap-1.5" data-id="element-19265">
                        <Image size={16} className="text-purple-400" data-id="element-19266" />
                        <span data-id="element-19267">Scene Settings</span>
                      </h3>
                      {/* Outfit selector */}
                      <div className="space-y-2" data-id="element-19268">
                        <label className="text-gray-300 text-sm" data-id="element-19269">Outfit</label>
                        <div className="grid grid-cols-2 gap-2" data-id="element-19270">
                          {['casual', 'elegant', 'intimate', 'sporty', 'fantasy', 'formal', 'business', 'party'].map(outfit => <button key={outfit} onClick={() => handleSceneChange('outfit', outfit)} className={`px-3 py-2 rounded-lg text-sm capitalize ${sceneSettings.outfit === outfit ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19271">
                              {outfit}
                            </button>)}
                        </div>
                      </div>
                      {/* Background selector */}
                      <div className="space-y-2" data-id="element-19272">
                        <label className="text-gray-300 text-sm" data-id="element-19273">
                          Background
                        </label>
                        <div className="grid grid-cols-2 gap-2" data-id="element-19274">
                          {['bedroom', 'living_room', 'outdoor', 'beach', 'club', 'fantasy', 'office', 'nightclub', 'spa', 'vr_space', 'penthouse', 'enchanted_forest'].map(bg => <button key={bg} onClick={() => handleSceneChange('background', bg)} className={`px-3 py-2 rounded-lg text-sm capitalize ${sceneSettings.background === bg ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19275">
                              {bg.replace(/_/g, ' ')}
                            </button>)}
                        </div>
                      </div>
                      {/* Lighting selector */}
                      <div className="space-y-2" data-id="element-19276">
                        <label className="text-gray-300 text-sm" data-id="element-19277">
                          Lighting
                        </label>
                        <div className="grid grid-cols-3 gap-2" data-id="element-19278">
                          {['soft', 'bright', 'dim', 'colorful', 'dramatic', 'natural', 'neon', 'golden', 'candlelight'].map(light => <button key={light} onClick={() => handleSceneChange('lighting', light)} className={`px-3 py-2 rounded-lg text-sm capitalize ${sceneSettings.lighting === light ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19279">
                              {light}
                            </button>)}
                        </div>
                      </div>
                      {/* Advanced Scene Settings */}
                      {showAdvancedSettings && <>
                          {/* Time of day */}
                          <div className="space-y-2" data-id="element-19280">
                            <label className="text-gray-300 text-sm" data-id="element-19281">
                              Time of Day
                            </label>
                            <div className="grid grid-cols-4 gap-2" data-id="element-19282">
                              {['morning', 'day', 'evening', 'night', 'sunset', 'twilight', 'timeless'].map(time => <button key={time} onClick={() => handleSceneChange('time', time)} className={`px-3 py-2 rounded-lg text-sm capitalize ${sceneSettings.time === time ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19283">
                                  {time}
                                </button>)}
                            </div>
                          </div>
                          {/* Weather */}
                          <div className="space-y-2" data-id="element-19284">
                            <label className="text-gray-300 text-sm" data-id="element-19285">
                              Weather
                            </label>
                            <div className="grid grid-cols-4 gap-2" data-id="element-19286">
                              {['clear', 'rainy', 'snowy', 'misty', 'sunny', 'stormy', 'indoor', 'magical'].map(weather => <button key={weather} onClick={() => handleSceneChange('weather', weather)} className={`px-3 py-2 rounded-lg text-sm capitalize ${sceneSettings.weather === weather ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19287">
                                  {weather}
                                </button>)}
                            </div>
                          </div>
                          {/* Atmosphere */}
                          <div className="space-y-2" data-id="element-19288">
                            <label className="text-gray-300 text-sm" data-id="element-19289">
                              Atmosphere
                            </label>
                            <div className="grid grid-cols-3 gap-2" data-id="element-19290">
                              {['cozy', 'tense', 'romantic', 'mysterious', 'peaceful', 'energetic', 'luxurious', 'futuristic', 'dystopian'].map(atmosphere => <button key={atmosphere} onClick={() => handleSceneChange('atmosphere', atmosphere)} className={`px-3 py-2 rounded-lg text-sm capitalize ${sceneSettings.atmosphere === atmosphere ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19291">
                                  {atmosphere}
                                </button>)}
                            </div>
                          </div>
                        </>}
                    </div>}
                  {activeTab === 'voice' && <div className="space-y-6" data-id="element-19292">
                      <h3 className="text-white font-medium flex items-center gap-1.5" data-id="element-19293">
                        <Mic size={16} className="text-purple-400" data-id="element-19294" />
                        <span data-id="element-19295">Voice Settings</span>
                      </h3>
                      {/* Voice tone */}
                      <div className="space-y-2" data-id="element-19296">
                        <label className="text-gray-300 text-sm" data-id="element-19297">Tone</label>
                        <div className="grid grid-cols-3 gap-2" data-id="element-19298">
                          {['friendly', 'seductive', 'playful', 'confident', 'gentle', 'intense', 'commanding', 'whisper', 'soothing'].map(tone => <button key={tone} onClick={() => handleVoiceChange('tone', tone)} className={`px-3 py-2 rounded-lg text-sm capitalize ${voiceSettings.tone === tone ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19299">
                              {tone}
                            </button>)}
                        </div>
                      </div>
                      {/* Accent */}
                      <div className="space-y-2" data-id="element-19300">
                        <label className="text-gray-300 text-sm" data-id="element-19301">Accent</label>
                        <div className="grid grid-cols-3 gap-2" data-id="element-19302">
                          {['american', 'british', 'australian', 'french', 'spanish', 'neutral', 'international'].map(accent => <button key={accent} onClick={() => handleVoiceChange('accent', accent)} className={`px-3 py-2 rounded-lg text-sm capitalize ${voiceSettings.accent === accent ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19303">
                              {accent}
                            </button>)}
                        </div>
                      </div>
                      {/* Voice pitch slider */}
                      <div className="space-y-2" data-id="element-19304">
                        <div className="flex justify-between" data-id="element-19305">
                          <label className="text-gray-300 text-sm" data-id="element-19306">Pitch</label>
                          <span className="text-purple-400 text-sm" data-id="element-19307">
                            {voiceSettings.pitch}%
                          </span>
                        </div>
                        <input type="range" min="0" max="100" value={voiceSettings.pitch} onChange={e => handleVoiceChange('pitch', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19308" />
                        <div className="flex justify-between text-xs text-gray-500" data-id="element-19309">
                          <span data-id="element-19310">Deeper</span>
                          <span data-id="element-19311">Higher</span>
                        </div>
                      </div>
                      {/* Voice speed slider */}
                      <div className="space-y-2" data-id="element-19312">
                        <div className="flex justify-between" data-id="element-19313">
                          <label className="text-gray-300 text-sm" data-id="element-19314">Speed</label>
                          <span className="text-purple-400 text-sm" data-id="element-19315">
                            {voiceSettings.speed}%
                          </span>
                        </div>
                        <input type="range" min="0" max="100" value={voiceSettings.speed} onChange={e => handleVoiceChange('speed', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19316" />
                        <div className="flex justify-between text-xs text-gray-500" data-id="element-19317">
                          <span data-id="element-19318">Slower</span>
                          <span data-id="element-19319">Faster</span>
                        </div>
                      </div>
                      {/* Advanced Voice Settings */}
                      {showAdvancedSettings && <>
                          {/* Emotional tone */}
                          <div className="space-y-2" data-id="element-19320">
                            <label className="text-gray-300 text-sm" data-id="element-19321">
                              Emotional Tone
                            </label>
                            <div className="grid grid-cols-3 gap-2" data-id="element-19322">
                              {['neutral', 'happy', 'sad', 'angry', 'passionate', 'calm', 'excited', 'intimate', 'powerful'].map(emotion => <button key={emotion} onClick={() => handleVoiceChange('emotion', emotion)} className={`px-3 py-2 rounded-lg text-sm capitalize ${voiceSettings.emotion === emotion ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19323">
                                  {emotion}
                                </button>)}
                            </div>
                          </div>
                          {/* Warmth slider */}
                          <div className="space-y-2" data-id="element-19324">
                            <div className="flex justify-between" data-id="element-19325">
                              <label className="text-gray-300 text-sm" data-id="element-19326">
                                Warmth
                              </label>
                              <span className="text-purple-400 text-sm" data-id="element-19327">
                                {voiceSettings.warmth}%
                              </span>
                            </div>
                            <input type="range" min="0" max="100" value={voiceSettings.warmth} onChange={e => handleVoiceChange('warmth', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19328" />
                            <div className="flex justify-between text-xs text-gray-500" data-id="element-19329">
                              <span data-id="element-19330">Cool</span>
                              <span data-id="element-19331">Warm</span>
                            </div>
                          </div>
                          {/* Clarity slider */}
                          <div className="space-y-2" data-id="element-19332">
                            <div className="flex justify-between" data-id="element-19333">
                              <label className="text-gray-300 text-sm" data-id="element-19334">
                                Clarity
                              </label>
                              <span className="text-purple-400 text-sm" data-id="element-19335">
                                {voiceSettings.clarity}%
                              </span>
                            </div>
                            <input type="range" min="0" max="100" value={voiceSettings.clarity} onChange={e => handleVoiceChange('clarity', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19336" />
                            <div className="flex justify-between text-xs text-gray-500" data-id="element-19337">
                              <span data-id="element-19338">Breathy</span>
                              <span data-id="element-19339">Clear</span>
                            </div>
                          </div>
                        </>}
                      {/* Language selection */}
                      <div className="space-y-2" data-id="element-19340">
                        <label className="text-gray-300 text-sm" data-id="element-19341">
                          Language
                        </label>
                        <div className="grid grid-cols-3 gap-2" data-id="element-19342">
                          {['english', 'spanish', 'french', 'german', 'japanese', 'multi'].map(lang => <button key={lang} onClick={() => handleVoiceChange('language', lang)} className={`px-3 py-2 rounded-lg text-sm capitalize ${voiceSettings.language === lang ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19343">
                              {lang === 'multi' ? 'Multilingual' : lang}
                            </button>)}
                        </div>
                      </div>
                      {/* Voice sample previews */}
                      <div className="space-y-3" data-id="element-19344">
                        <h4 className="text-white text-sm font-medium" data-id="element-19345">
                          Voice Samples
                        </h4>
                        <div className="grid grid-cols-2 gap-2" data-id="element-19346">
                          {voicePresets.slice(0, 6).map(preset => <button key={preset.id} onClick={() => toggleVoicePreview(preset.id)} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${activeVoiceSample === preset.id ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19347">
                              <span data-id="element-19348">{preset.name}</span>
                              {activeVoiceSample === preset.id && isPlaying ? <Pause size={14} data-id="element-19349" /> : <Play size={14} data-id="element-19350" />}
                            </button>)}
                        </div>
                      </div>
                    </div>}
                  {activeTab === 'mood' && <div className="space-y-6" data-id="element-19351">
                      <h3 className="text-white font-medium flex items-center gap-1.5" data-id="element-19352">
                        <CloudLightning size={16} className="text-purple-400" data-id="element-19353" />
                        <span data-id="element-19354">Mood Settings</span>
                      </h3>
                      <p className="text-gray-400 text-sm" data-id="element-19355">
                        Mood presets combine emotional states and response
                        patterns for a dynamic personality.
                      </p>
                      {/* Primary mood selection */}
                      <div className="space-y-2" data-id="element-19356">
                        <label className="text-gray-300 text-sm" data-id="element-19357">
                          Primary Mood
                        </label>
                        <div className="grid grid-cols-3 gap-2" data-id="element-19358">
                          {['playful', 'passionate', 'dominant', 'shy', 'cold', 'romantic', 'teasing', 'mysterious', 'friendly'].map(mood => <button key={mood} onClick={() => handleMoodChange('primary', mood)} className={`px-3 py-2 rounded-lg text-sm capitalize ${moodSettings.primary === mood ? 'bg-purple-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`} data-id="element-19359">
                              {mood}
                            </button>)}
                        </div>
                      </div>
                      {/* Mood intensity slider */}
                      <div className="space-y-2" data-id="element-19360">
                        <div className="flex justify-between" data-id="element-19361">
                          <label className="text-gray-300 text-sm" data-id="element-19362">
                            Intensity
                          </label>
                          <span className="text-purple-400 text-sm" data-id="element-19363">
                            {moodSettings.intensity}%
                          </span>
                        </div>
                        <input type="range" min="0" max="100" value={moodSettings.intensity} onChange={e => handleMoodChange('intensity', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19364" />
                        <div className="flex justify-between text-xs text-gray-500" data-id="element-19365">
                          <span data-id="element-19366">Subtle</span>
                          <span data-id="element-19367">Intense</span>
                        </div>
                      </div>
                      {/* Advanced Mood Settings */}
                      {showAdvancedSettings && <>
                          {/* Mood stability slider */}
                          <div className="space-y-2" data-id="element-19368">
                            <div className="flex justify-between" data-id="element-19369">
                              <label className="text-gray-300 text-sm" data-id="element-19370">
                                Stability
                              </label>
                              <span className="text-purple-400 text-sm" data-id="element-19371">
                                {moodSettings.stability}%
                              </span>
                            </div>
                            <input type="range" min="0" max="100" value={moodSettings.stability} onChange={e => handleMoodChange('stability', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19372" />
                            <div className="flex justify-between text-xs text-gray-500" data-id="element-19373">
                              <span data-id="element-19374">Volatile</span>
                              <span data-id="element-19375">Stable</span>
                            </div>
                          </div>
                          {/* Openness slider */}
                          <div className="space-y-2" data-id="element-19376">
                            <div className="flex justify-between" data-id="element-19377">
                              <label className="text-gray-300 text-sm" data-id="element-19378">
                                Openness
                              </label>
                              <span className="text-purple-400 text-sm" data-id="element-19379">
                                {moodSettings.openness}%
                              </span>
                            </div>
                            <input type="range" min="0" max="100" value={moodSettings.openness} onChange={e => handleMoodChange('openness', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19380" />
                            <div className="flex justify-between text-xs text-gray-500" data-id="element-19381">
                              <span data-id="element-19382">Reserved</span>
                              <span data-id="element-19383">Open</span>
                            </div>
                          </div>
                          {/* Responsiveness slider */}
                          <div className="space-y-2" data-id="element-19384">
                            <div className="flex justify-between" data-id="element-19385">
                              <label className="text-gray-300 text-sm" data-id="element-19386">
                                Responsiveness
                              </label>
                              <span className="text-purple-400 text-sm" data-id="element-19387">
                                {moodSettings.responsiveness}%
                              </span>
                            </div>
                            <input type="range" min="0" max="100" value={moodSettings.responsiveness} onChange={e => handleMoodChange('responsiveness', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19388" />
                            <div className="flex justify-between text-xs text-gray-500" data-id="element-19389">
                              <span data-id="element-19390">Aloof</span>
                              <span data-id="element-19391">Reactive</span>
                            </div>
                          </div>
                          {/* Adaptability slider */}
                          <div className="space-y-2" data-id="element-19392">
                            <div className="flex justify-between" data-id="element-19393">
                              <label className="text-gray-300 text-sm" data-id="element-19394">
                                Adaptability
                              </label>
                              <span className="text-purple-400 text-sm" data-id="element-19395">
                                {moodSettings.adaptability}%
                              </span>
                            </div>
                            <input type="range" min="0" max="100" value={moodSettings.adaptability} onChange={e => handleMoodChange('adaptability', parseInt(e.target.value))} className="w-full h-2 rounded-lg appearance-none bg-gray-700 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500" data-id="element-19396" />
                            <div className="flex justify-between text-xs text-gray-500" data-id="element-19397">
                              <span data-id="element-19398">Rigid</span>
                              <span data-id="element-19399">Adaptive</span>
                            </div>
                          </div>
                        </>}
                      {/* Show summary of selected preset */}
                      {selectedPreset && <div className="bg-gray-800/50 rounded-lg p-4 border border-purple-500/30" data-id="element-19400">
                          <h4 className="text-white font-medium mb-2 flex items-center gap-1.5" data-id="element-19401">
                            <CloudLightning size={14} className="text-purple-400" data-id="element-19402" />
                            <span data-id="element-19403">{selectedPreset.name} Mood Profile</span>
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs" data-id="element-19404">
                            <div data-id="element-19405">
                              <p className="text-gray-400 mb-1" data-id="element-19406">Personality</p>
                              <ul className="text-gray-300 space-y-1" data-id="element-19407">
                                {selectedPreset.traits && Object.entries(selectedPreset.traits).map(([trait, value]) => <li key={trait} className="flex justify-between" data-id="element-19408">
                                        <span className="capitalize" data-id="element-19409">
                                          {trait}
                                        </span>
                                        <span className="text-purple-400" data-id="element-19410">
                                          {value}%
                                        </span>
                                      </li>).slice(0, 3)}
                              </ul>
                            </div>
                            <div data-id="element-19411">
                              <p className="text-gray-400 mb-1" data-id="element-19412">Scene</p>
                              <ul className="text-gray-300 space-y-1" data-id="element-19413">
                                {selectedPreset.sceneProps && Object.entries(selectedPreset.sceneProps).map(([prop, value]) => <li key={prop} className="flex justify-between" data-id="element-19414">
                                        <span className="capitalize" data-id="element-19415">
                                          {prop.replace(/_/g, ' ')}
                                        </span>
                                        <span className="text-purple-400 capitalize" data-id="element-19416">
                                          {String(value).replace(/_/g, ' ')}
                                        </span>
                                      </li>).slice(0, 3)}
                              </ul>
                            </div>
                            <div data-id="element-19417">
                              <p className="text-gray-400 mb-1" data-id="element-19418">Voice</p>
                              <ul className="text-gray-300 space-y-1" data-id="element-19419">
                                {selectedPreset.voiceProps && Object.entries(selectedPreset.voiceProps).map(([prop, value]) => <li key={prop} className="flex justify-between" data-id="element-19420">
                                        <span className="capitalize" data-id="element-19421">
                                          {prop}
                                        </span>
                                        <span className="text-purple-400 capitalize" data-id="element-19422">
                                          {typeof value === 'number' ? `${value}%` : String(value).replace(/_/g, ' ')}
                                        </span>
                                      </li>).slice(0, 3)}
                              </ul>
                            </div>
                          </div>
                          {/* Mood badge & effect */}
                          {(selectedPreset.badge || selectedPreset.effect) && <div className="mt-3 flex gap-3" data-id="element-19423">
                              {selectedPreset.badge && <div className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 rounded text-xs text-purple-400 border border-purple-500/30" data-id="element-19424">
                                  <BadgeCheck size={12} data-id="element-19425" />
                                  <span data-id="element-19426">{selectedPreset.badge} Badge</span>
                                </div>}
                              {selectedPreset.effect && <div className="flex items-center gap-1 px-2 py-1 bg-cyan-500/20 rounded text-xs text-cyan-400 border border-cyan-500/30" data-id="element-19427">
                                  <Sparkles size={12} data-id="element-19428" />
                                  <span data-id="element-19429">
                                    {selectedPreset.effect.charAt(0).toUpperCase() + selectedPreset.effect.slice(1)}{' '}
                                    Effect
                                  </span>
                                </div>}
                            </div>}
                        </div>}
                      {/* Mood explanation */}
                      <div className="p-3 rounded-lg bg-black/30 border border-purple-500/20" data-id="element-19430">
                        <h4 className="text-white text-sm font-medium mb-2 flex items-center gap-1.5" data-id="element-19431">
                          <Lightbulb size={14} className="text-yellow-400" data-id="element-19432" />
                          <span data-id="element-19433">Live Mood Shift</span>
                        </h4>
                        <p className="text-gray-300 text-xs" data-id="element-19434">
                          The mood shift will dynamically affect how the AI
                          responds in chat. Higher intensity creates more
                          dramatic personality changes that will be visible to
                          everyone in the playroom.
                        </p>
                      </div>
                    </div>}
                </> :
            // Preview mode content
            <div className="flex flex-col items-center justify-center h-full" ref={previewRef} data-id="element-19435">
                  {/* Preview animation */}
                  <div className={`relative ${fullscreenPreview ? 'w-96 h-96' : 'w-64 h-64'} mb-6 transition-all duration-300`} data-id="element-19436">
                    {/* Placeholder for actual preview animation */}
                    <div className="absolute inset-0 rounded-full bg-gradient-to-r from-purple-600 to-pink-600 opacity-20 animate-pulse" data-id="element-19437"></div>
                    <div className="absolute inset-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 opacity-30 animate-ping" style={{
                  animationDuration: '3s'
                }} data-id="element-19438"></div>
                    <div className="absolute inset-8 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 opacity-40 animate-pulse" style={{
                  animationDuration: '2s'
                }} data-id="element-19439"></div>
                    {/* Angel silhouette with effect based on selected preset */}
                    <div className="absolute inset-0 flex items-center justify-center" data-id="element-19440">
                      <div className={`relative ${selectedPreset?.effect ? `effect-${selectedPreset.effect}` : ''}`} data-id="element-19441">
                        <PersonStanding size={fullscreenPreview ? 150 : 100} className="text-white/70" data-id="element-19442" />
                        {/* Effect overlays */}
                        {selectedPreset?.effect === 'sparkle' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-19443">
                            <Sparkles size={fullscreenPreview ? 180 : 120} className="text-yellow-400/50 animate-pulse" data-id="element-19444" />
                          </div>}
                        {selectedPreset?.effect === 'shadow' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-19445">
                            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/50 to-transparent rounded-full" data-id="element-19446"></div>
                          </div>}
                        {selectedPreset?.effect === 'glow' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-19447">
                            <div className="absolute inset-0 bg-pink-500/20 rounded-full blur-xl animate-pulse" data-id="element-19448"></div>
                          </div>}
                        {selectedPreset?.effect === 'pulse' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-19449">
                            <div className="absolute inset-0 bg-red-500/10 rounded-full animate-ping" data-id="element-19450"></div>
                          </div>}
                        {selectedPreset?.effect === 'magical' && <div className="absolute inset-0 flex items-center justify-center overflow-hidden" data-id="element-19451">
                            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 via-cyan-500/20 to-pink-500/20 rounded-full animate-spin-slow" data-id="element-19452"></div>
                          </div>}
                        {selectedPreset?.effect === 'neon' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-19453">
                            <div className="absolute inset-0 bg-cyan-500/30 rounded-full blur-md" data-id="element-19454"></div>
                            <div className="absolute inset-0 border-2 border-cyan-500/50 rounded-full" data-id="element-19455"></div>
                          </div>}
                        {selectedPreset?.effect === 'frost' && <div className="absolute inset-0 flex items-center justify-center" data-id="element-19456">
                            <div className="absolute inset-0 bg-blue-300/20 rounded-full blur-md" data-id="element-19457"></div>
                            <Snowflake size={fullscreenPreview ? 180 : 120} className="text-blue-300/40 absolute" data-id="element-19458" />
                          </div>}
                      </div>
                    </div>
                    {/* Particle effects */}
                    <div className="absolute inset-0" data-id="element-19459">
                      {Array.from({
                    length: 20
                  }).map((_, i) => <div key={i} className="absolute w-2 h-2 rounded-full bg-purple-400" style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    opacity: Math.random() * 0.7 + 0.3,
                    transform: `scale(${Math.random() * 0.5 + 0.5})`,
                    animation: `float-particle 3s infinite`,
                    animationDelay: `${Math.random() * 3}s`,
                    animationTimingFunction: 'ease-out'
                  }} data-id="element-19460" />)}
                    </div>
                    {/* Badge display */}
                    {selectedPreset?.badge && <div className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 bg-purple-500/70 rounded-full text-white text-xs font-medium flex items-center gap-1.5" data-id="element-19461">
                        <BadgeCheck size={12} data-id="element-19462" />
                        <span data-id="element-19463">{selectedPreset.badge}</span>
                      </div>}
                    {/* BlackMirror AI watermark */}
                    <div className="absolute bottom-4 left-0 right-0 text-center" data-id="element-19464">
                      <span className="px-2 py-1 bg-black/70 rounded text-white text-xs font-medium flex items-center gap-1 justify-center w-max mx-auto" data-id="element-19465">
                        <Brain size={10} className="text-purple-400" data-id="element-19466" />
                        <span data-id="element-19467">BlackMirror AI</span>
                      </span>
                    </div>
                    {/* Fullscreen toggle */}
                    <button onClick={toggleFullscreenPreview} className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full bg-black/50 text-white" data-id="element-19468">
                      {fullscreenPreview ? <Minimize size={14} data-id="element-19469" /> : <Maximize size={14} data-id="element-19470" />}
                    </button>
                  </div>
                  {/* Loading bar */}
                  <div className="w-full max-w-md h-2 bg-gray-800 rounded-full overflow-hidden mb-4" data-id="element-19471">
                    <div className="h-full bg-gradient-to-r from-purple-600 to-pink-600" style={{
                  width: `${animationProgress}%`
                }} data-id="element-19472"></div>
                  </div>
                  <h3 className="text-white font-medium text-lg mb-2" data-id="element-19473">
                    {showConfirmation ? 'BlackMirror ShapeShift Complete!' : 'Applying BlackMirror ShapeShift...'}
                  </h3>
                  <p className="text-gray-300 text-sm text-center mb-6" data-id="element-19474">
                    {showConfirmation ? `${angelName} has been transformed with the ${selectedPreset?.name || 'custom'} ${activeTab} shift.` : `Transforming ${angelName}'s ${activeTab} using BlackMirror AI...`}
                  </p>
                  {/* Confirmation buttons */}
                  {showConfirmation && <div className="flex gap-4" data-id="element-19475">
                      <button onClick={handleCancel} className="px-6 py-3 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm transition-colors" data-id="element-19476">
                        Cancel
                      </button>
                      <button onClick={handleConfirm} className="flex items-center gap-2 px-6 py-3 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-medium transition-colors" data-id="element-19477">
                        <Check size={18} data-id="element-19478" />
                        <span data-id="element-19479">Apply ShapeShift</span>
                        <span className="ml-1 text-xs opacity-80" data-id="element-19480">
                          ({calculateShiftCost()}{' '}
                          <Gem size={10} className="inline text-purple-300" data-id="element-19481" />)
                        </span>
                      </button>
                    </div>}
                </div>}
            </div>
          </div>
          {/* Footer with action buttons */}
          {!isPreviewMode && <div className="p-4 border-t border-white/10 bg-black/30" data-id="element-19482">
              <div className="flex items-center justify-between" data-id="element-19483">
                <div className="flex items-center gap-4" data-id="element-19484">
                  {/* Credit display */}
                  <div className="flex items-center gap-2" data-id="element-19485">
                    <Gem size={18} className="text-purple-400" data-id="element-19486" />
                    <span className="text-white font-medium" data-id="element-19487">
                      {userCredits.desireGems}
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-19488">
                    <Coins size={18} className="text-yellow-400" data-id="element-19489" />
                    <span className="text-white font-medium" data-id="element-19490">
                      {userCredits.luvTokens}
                    </span>
                  </div>
                  {/* Cost indicator */}
                  <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-gray-800 text-white text-xs" data-id="element-19491">
                    <span data-id="element-19492">Cost:</span>
                    <Gem size={12} className="text-purple-400 ml-1" data-id="element-19493" />
                    <span data-id="element-19494">{calculateShiftCost()}</span>
                    <span className="mx-1" data-id="element-19495">or</span>
                    <Coins size={12} className="text-yellow-400" data-id="element-19496" />
                    <span data-id="element-19497">{calculateShiftCost() * 10}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3" data-id="element-19498">
                  <button onClick={handleCancel} className="px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white text-sm transition-colors" data-id="element-19499">
                    Cancel
                  </button>
                  <button onClick={handleApply} disabled={isProcessing} className={`flex items-center gap-2 px-6 py-2 rounded-lg text-white text-sm font-medium transition-all ${isProcessing ? 'bg-gray-700 cursor-not-allowed' : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg hover:shadow-purple-600/20'}`} data-id="element-19500">
                    {isProcessing ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-19501"></div>
                        <span data-id="element-19502">Processing...</span>
                      </> : <>
                        <Zap size={16} data-id="element-19503" />
                        <span data-id="element-19504">Apply BlackMirror ShapeShift</span>
                        <span className="ml-1 text-xs opacity-80" data-id="element-19505">
                          ({calculateShiftCost()}{' '}
                          <Gem size={10} className="inline text-purple-300" data-id="element-19506" />)
                        </span>
                      </>}
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
};